import { useQuery } from "@tanstack/react-query";
import { ApiError, get } from "./client";

/** `/api/v1/dashboard`: where everything stands, as numbers with links.
 *
 * The office's queues and its money arrive already counted and priced -- the
 * rules for what is "waiting" or "on order" are `prv.workflow.queues`, and the
 * screen only lays them out. A crew account gets an empty queue and no money,
 * because both are office-wide figures. */

export type QueueTone = "warn" | "ok" | "crit" | "quiet";

export interface QueueTile {
  key: string;
  label: string;
  value: number;
  /** What to do about it, as the link's words: "Review and commit". */
  action: string;
  href: string;
  tone: QueueTone;
}

export interface MoneyStep {
  key: string;
  label: string;
  /** Excluding VAT, as a decimal string: "412880.00". */
  amount: string;
  count: number;
  /** What `count` counts, singular: "quotation", "PO". */
  noun: string;
}

export interface GroupCounts {
  name: string;
  counts: Record<string, number>;
}

export interface Dashboard {
  today: string;
  contract_codes: string[];
  total_sites: number;
  unlocated_sites: number;
  office_queue: QueueTile[];
  commercial: { steps: MoneyStep[] } | null;
  status_tiles: { label: string; value: number; href: string; status: string }[];
  /** The older "in flight" tiles, still served; the queue above replaces them here. */
  pipeline: { label: string; value: number; href: string; tone?: string }[];
  map_sites: { id: number; name: string; status: string; label: string; lat: number; lon: number }[];
  status_key: { status: string; label: string; color: string }[];
  by_region: GroupCounts[];
  by_cluster: GroupCounts[];
  hidden_clusters: number;
}

/** Same key as the older hook in `delivery.ts`, so an upload or a commit that
 * invalidates "dashboard" refreshes this too. */
export function useDashboard() {
  return useQuery<Dashboard, ApiError>({
    queryKey: ["dashboard"],
    queryFn: () => get<Dashboard>("/api/v1/dashboard"),
  });
}
